/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。
 */
import {
  type SplitSupplyNoticeDownloadFormat,
  type SplitSupplyNoticeListItem,
  SplitSupplyNoticeSection,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router'

import {
  HStack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { ROW_ACTION_GAP } from '~/components/common/MasterDataTable/stickyActionColumn'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { convertAreaLabel } from '~/models/area'
import { protoDateToCivilDate } from '~/models/civilDate'
import { formatDateDisplay } from '~/models/dateTime'
import { NoticeDownloadMenu } from '~/pages/SplitSupplyPlan/components/NoticeDownloadMenu'
import {
  noticeDetailSearch,
  noticeRowKey,
} from '~/pages/SplitSupplyPlan/models/noticeKey'
import { routePaths } from '~/routes/routePaths'

type Props = {
  rows: SplitSupplyNoticeListItem[]
  isLoading: boolean
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
  onDownload: (
    item: SplitSupplyNoticeListItem,
    format: SplitSupplyNoticeDownloadFormat,
  ) => void
}

const NO_VALUE = '—'

const detailPath = (item: SplitSupplyNoticeListItem): string => {
  const target = item.key?.targetDate
  const civil = protoDateToCivilDate(target)
  if (item.key === undefined || civil === undefined) {
    return routePaths.planSplitSupply.path
  }
  return `${routePaths.planSplitSupplyDetail.path}?${noticeDetailSearch(item.key, civil)}`
}

/**
 * 自社の通告値の一覧。 1 行 = 電文 1 通 (断面 × 対象日 × エリア × 自社の事業者 × 負荷追随分供給者 ×
 * 負荷追随分の契約識別番号 1)。 行クリックで詳細。 並びは backend が返す順
 * (対象期間 → エリア → 自社の事業者 → 負荷追随分供給者 → 契約識別番号 1)。
 */
export const SplitSupplyNoticeTable = ({
  rows,
  isLoading,
  errorLabel,
  onDownload,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns: Array<MasterDataTableColumn<SplitSupplyNoticeListItem>> = [
    {
      key: 'period',
      header: t('page:splitSupplyPlan.column.period'),
      nowrap: true,
      cell: (row) => {
        const key = row.key
        const target = protoDateToCivilDate(key?.targetDate)
        if (key === undefined || target === undefined) return NO_VALUE
        return key.section === SplitSupplyNoticeSection.MONTHLY
          ? t('page:splitSupplyPlan.monthLabel', {
              year: target.year,
              month: target.month,
            })
          : formatDateDisplay(
              new Date(target.year, target.month - 1, target.day),
            )
      },
    },
    {
      key: 'area',
      header: t('page:splitSupplyPlan.column.area'),
      cell: (row) =>
        row.key === undefined ? NO_VALUE : convertAreaLabel(row.key.areaCode),
    },
    {
      key: 'own',
      header: t('page:splitSupplyPlan.column.ownBusiness'),
      cell: (row) => <BusinessCell business={row.ownBusiness} />,
    },
    {
      key: 'loadFollowing',
      header: (
        <HintTooltip
          content={t('page:splitSupplyPlan.columnHint.loadFollowing')}
        >
          <span>{t('page:splitSupplyPlan.column.loadFollowing')}</span>
        </HintTooltip>
      ),
      cell: (row) => <BusinessCell business={row.loadFollowing} />,
    },
    {
      key: 'contractId',
      header: t('page:splitSupplyPlan.column.loadFollowingContractId'),
      nowrap: true,
      cell: (row) => row.key?.loadFollowingContractId ?? NO_VALUE,
    },
    {
      key: 'points',
      header: t('page:splitSupplyPlan.column.points'),
      align: 'right',
      format: 'number',
      cell: (row) => (
        <HintTooltip
          content={row.points
            .map((p) =>
              p.customerName === ''
                ? p.supplyPointSpecificNumber
                : `${p.customerName} (${p.supplyPointSpecificNumber})`,
            )
            .join('\n')}
        >
          <span>
            {t('page:splitSupplyPlan.pointCount', { count: row.points.length })}
          </span>
        </HintTooltip>
      ),
    },
    {
      key: 'created',
      header: t('page:splitSupplyPlan.column.created'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.createdAt} updatedBy={row.createdBy} />
      ),
    },
    {
      key: 'updated',
      header: t('page:splitSupplyPlan.column.updated'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      // 「ダウンロード」(文字 + caret) + 「詳細」が収まる幅
      actionColWidth="240px"
      columns={columns}
      emptyLabel={t('page:splitSupplyPlan.empty')}
      errorLabel={errorLabel}
      getRowKey={(row) => (row.key === undefined ? '' : noticeRowKey(row.key))}
      isLoading={isLoading}
      onRowClick={(row, e) => {
        openOrNavigate(navigate, detailPath(row), e)
      }}
      renderRowActions={(row) => (
        <HStack gap={ROW_ACTION_GAP}>
          <NoticeDownloadMenu
            onDownload={(format) => {
              onDownload(row, format)
            }}
            pointCount={row.points.length}
          />
          <TertiaryButton
            // 行アクションの文字は md (14px) 維持 (ProcurementPlanTable の「詳細」と同じ判断)
            fontSize="md"
            asChild
            paddingX={3}
          >
            <RouterLink to={detailPath(row)}>
              {t('page:splitSupplyPlan.actionDetail')}
            </RouterLink>
          </TertiaryButton>
        </HStack>
      )}
      rows={rows}
    />
  )
}

const BusinessCell = ({
  business,
}: {
  business: { businessCode: string; businessName: string } | undefined
}): React.ReactElement =>
  business === undefined ? (
    <span>{NO_VALUE}</span>
  ) : (
    <VStack alignItems="flex-start" gap={0}>
      <Text as="span">{business.businessCode}</Text>
      {business.businessName !== '' && (
        <Text as="span" textStyle="caption" color="Text.lowEmphasis">
          {business.businessName}
        </Text>
      )}
    </VStack>
  )
